/**
 * THE pill (design.md §1 "semantic state scales", findings #27 #38 #56 #57 #58).
 *
 * One component paints every state, option and maturity value in the app:
 *  - the label color is DERIVED from the fill (lib/color.pillColors), never chosen per screen,
 *    so any color an option row carries is readable in both modes;
 *  - a pill never wraps; a column passes one `width` so every pill in it is the same size;
 *  - "not final" states (deferred, pending, exploratory) draw a dashed edge;
 *  - Unassessed is the kit EmptyValue's word (R16 T-137: no dashed pill) — distinct from a real 0.
 *
 * Maturity renders either as a heat pill or as a five-slot dot row where the level IS the
 * count: 0 fills none, 5 fills all, and null reads Unassessed (#57; R16 T-137).
 */
import type { CSSProperties, ReactNode } from 'react';
import { isHex, pillColors } from '@/lib/color';
import type { HeatScale } from '@/lib/maturity';
import { cn } from '@/lib/utils';
import { EmptyValue } from '@/kit/EmptyValue';

/** Semantic roles for options that carry `meta.color_role` instead of a hex. */
export type PillRole = 'success' | 'notice' | 'critical' | 'interactive' | 'neutral' | 'muted';

const ROLE_CLASS: Record<PillRole, string> = {
  success: 'bg-success-fill text-success-fill-foreground',
  notice: 'bg-notice text-notice-foreground',
  critical: 'bg-critical-fill text-critical-fill-foreground',
  interactive: 'bg-interactive-tint text-link',
  neutral: 'bg-paper-2 text-ink-2',
  muted: 'bg-paper-2 text-ink-2',
};

/** `option.meta.color_role` values the seed writes, mapped to pill roles. */
export function roleFromColorRole(colorRole: unknown): PillRole | undefined {
  switch (colorRole) {
    case 'good':
    case 'success':
      return 'success';
    case 'warning':
    case 'notice':
      return 'notice';
    case 'critical':
    case 'bad':
      return 'critical';
    case 'series':
    case 'interactive':
      return 'interactive';
    case 'muted':
      return 'muted';
    default:
      return undefined;
  }
}

export interface StatePillProps {
  label: ReactNode;
  /** A hex from the data (option.color, workflow_state.color). Wins over `role`. */
  color?: string | null;
  role?: PillRole;
  /** Dashed edge: the state is not final (deferred, pending, exploratory). */
  dashed?: boolean;
  /** One width for every pill in a column (#58). A number is px. */
  width?: number | string;
  title?: string;
  className?: string;
}

const BASE =
  'inline-flex h-[var(--pill-h)] max-w-full shrink-0 items-center justify-center overflow-hidden whitespace-nowrap rounded-full border border-transparent px-2.5 text-[length:var(--t-eyebrow)] font-semibold leading-[14px] motion-fast transition-colors';

export function StatePill({ label, color, role, dashed, width, title, className }: StatePillProps) {
  const style: CSSProperties = {};
  if (width !== undefined) style.width = width;
  let roleClass = ROLE_CLASS[role ?? 'neutral'];
  if (isHex(color)) {
    const c = pillColors(color);
    style.backgroundColor = c.bg;
    style.color = c.fg;
    roleClass = '';
    if (dashed) style.borderColor = c.fg;
  }
  return (
    <span
      className={cn(BASE, roleClass, dashed && 'border-dashed border-current', className)}
      style={style}
      title={title ?? (typeof label === 'string' ? label : undefined)}
    >
      <span className="truncate">{label}</span>
    </span>
  );
}

/** Five slots, level = count. null → null (Unassessed). Out-of-range levels clamp. */
export function maturitySlots(level: number | null | undefined, slots = 5): boolean[] | null {
  if (level === null || level === undefined || !Number.isFinite(level)) return null;
  const filled = Math.max(0, Math.min(slots, Math.round(level)));
  return Array.from({ length: slots }, (_, i) => i < filled);
}

export function MaturityDots({ level, className }: { level: number | null | undefined; className?: string }) {
  const slots = maturitySlots(level);
  // The label is built from the clamped slot count (B4), never the raw `level` — a stray 7 or
  // -1 in the data must not read "Maturity 7 of 5" while the dots themselves show 5 filled.
  const label = slots ? `Maturity ${slots.filter(Boolean).length} of 5` : 'Maturity unassessed';
  if (!slots) return <EmptyValue word="Unassessed" variant="field" title={label} className={className} />;
  return (
    <span className={cn('inline-flex items-center gap-[3px]', className)} role="img" aria-label={label} title={label}>
      {slots.map((on, i) => (
        <span
          key={i}
          data-on={on || undefined}
          className={cn(
            'inline-block h-2 w-2 rounded-full border',
            on ? 'border-interactive bg-interactive' : 'border-rule-strong',
          )}
        />
      ))}
    </span>
  );
}

/** A maturity value as a heat pill: the option row's color when it has one, else the heat token. */
export function MaturityPill({
  level,
  scale,
  width = 104,
  className,
}: {
  level: number | null | undefined;
  scale: HeatScale;
  width?: number | string;
  className?: string;
}) {
  if (level === null || level === undefined) return <EmptyValue word="Unassessed" variant="field" title="No value recorded — distinct from a real 0" className={className} />;
  const stop = scale.at(level);
  const label = stop?.label ?? String(level);
  if (stop && isHex(stop.bg)) return <StatePill label={label} color={stop.bg} width={width} className={className} />;
  return (
    <span
      className={cn(BASE, className)}
      style={{ width, backgroundColor: stop?.bg ?? 'var(--paper-2)', color: stop?.ink ?? 'var(--muted-foreground)' }}
      title={label}
    >
      <span className="truncate">{label}</span>
    </span>
  );
}
